import {Badge, Button, Col, Modal, Row, Table} from "antd";
import Title from "antd/es/typography/Title";
import {format} from "date-fns";
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {IoAddOutline} from "react-icons/io5";
import {useNavigate, useParams} from "react-router-dom";
import {RoundButtonWithIcon} from "../../../components/AntCostomComponents/Buttons";
import {UserContext} from "../../../context/UserContext";
import {
    createReportRequest,
    getPdfReportByPatient,
    getReportByPatient,
    getReportRequestsByPatient
} from "../../../utils/api-lists/patient.api";
import {formatDate, formatUtcToLocal} from "../../../utils/date.util";


const ReportTable = () => {
    const {patientId} = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation("common");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const {user} = useContext(UserContext);
    const [showPopup, setShowPopup] = useState(false);


    const [reports, setReports] = useState();
    const [requestReports, setRequestReports] = useState([]);
    const [isRequestCreated, setIsRequestCreated] = useState(false);

    const statusMap = {
        REQUESTED: {
            label: t("requested"),
            color: "#7f8c8d",
        },
        IN_PROGRESS: {
            label: t("in_progress"),
            color: "#f39c12",
        },
        COMPLETED: {
            label: t("completed"),
            color: "#2ecc71",
        },
        PDF: {
            label: t("pdf"),
            color: "#3498db",
        }
    };

    const reportStatusMap = {
        DRAFT: {
            label: t("in_progress"),
            color: "#e67e22",
        },
        COMPLETED: {
            label: t("completed"),
            color: "#2ecc71",
        },
        PDF: {
            label: t("pdf"),
            color: "#3498db",
        }
    }

    const mapPdfReports = (reports) => {
        //     map all pdf reports to the same structure as other reports
        return reports?.map((report) => ({
            ...report,
            id: report.id,
            status: "PDF",
            updated_at: report.created_at,
            doctor: {
                firstName: 'Generated',
                lastName: '',
            },
            pdf_url: report.url,
        })) || [];
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const reportPayload = await getReportByPatient(patientId);
                const pdfReportPayload = await getPdfReportByPatient(patientId);
                const requestPayload = await getReportRequestsByPatient(patientId);
                setRequestReports(requestPayload.report_requests);


                const allReports = reportPayload.reports || [];
                if (pdfReportPayload.pdf_reports) {
                    const pdfReports = mapPdfReports(pdfReportPayload.pdf_reports);
                    allReports.push(...pdfReports);
                }
                allReports.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
                console.log('allReports', allReports);
                setReports(allReports);
            } catch (error) {
                setError(error.message);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isRequestCreated, patientId]);

    const createNewReportRequest = async () => {
        try {
            await createReportRequest(patientId, user.id);
            setIsRequestCreated(true);
        } catch (error) {
            setIsRequestCreated(true);
            setError(error.message);
        }
    }


    const requestReportColumn = [
        {
            title: t("no"),
            key: "index",
            responsive: ["md"],
            render: (_, __, index) => (
                <span className="text-white text-sm">{index + 1}</span>
            ),
        },
        {
            title: t("date"),
            dataIndex: "requested_at",
            key: "date",
            render: (date) => (
                <span className="text-gray-300 text-sm">
                    <span className="text-white text-sm">{formatDate(date).toLocaleUpperCase()}</span>

                </span>
            ),
        },
        {
            title: t("status"),
            dataIndex: "status",
            key: "status",
            render: (status) => {

                const badgeStyle = {
                    backgroundColor: statusMap[status]?.color || "#bdc3c7",
                    borderRadius: "9999px",
                    padding: "0 8px",
                    height: "auto",
                    lineHeight: "20px",
                };

                return (
                    <Badge
                        count={
                            <span className="text-white text-xs px-2 py-1 rounded-xl">
                                {statusMap[status]?.label || status}
                            </span>
                        }
                        style={badgeStyle}
                    />
                );
            },
        },
    ]

    const reportColumns = [
        {
            title: <span className="text-gray-400">{t("no")}</span>,
            key: "index",
            responsive: ["md"],
            render: (_, __, index) => (
                <span className="text-white text-sm">{index + 1}</span>
            ),
        },
        {
            title: <span className="text-gray-400">{t("date")}</span>,
            dataIndex: "updated_at",
            key: "date",
            render: (date) => (
                <span className="text-gray-300 text-sm">
                    {date ? (
                        (() => {
                            try {
                                const parsedDate = new Date(date);
                                return !isNaN(parsedDate.getTime())
                                    ? format(parsedDate, "MM-dd-yyyy HH:mma")
                                    : "Invalid date";
                            } catch (error) {
                                return "Invalid date";
                            }
                        })()
                    ) : "No date"}
                </span>
            ),
        },
        {
            title: <span className="text-gray-400">{t("doctor")}</span>,
            key: "doctor",
            responsive: ["md"],
            render: () => (
                <span className="text-gray-300 text-sm">
                    {user.firstName} {user.lastName}
                </span>
            ),
        },
        {
            title: <span className="text-gray-400">{t("status")}</span>,
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Badge
                    count={
                        <span className="text-white text-xs px-2 py-1 rounded-xl">
                            {reportStatusMap[status]?.label || status}
                        </span>
                    }
                    style={{
                        backgroundColor: reportStatusMap[status]?.color || "#bdc3c7",
                        borderRadius: "9999px",
                        padding: "0 8px",
                        height: "auto",
                        lineHeight: "20px",
                    }}
                />
            ),
        },
    ];

    const dataSource = reports?.map((report, index) => ({
        ...report,
        key: report.id,
        index,
    }));

    const reportRequestdataSource = requestReports?.map((report, index) => ({
        ...report,
        requested_at:formatUtcToLocal(report.requested_at),
        key: report.id,
        index,
    }))

    const onRowClick = (record) => {
        if (record.status === "REQUESTED") {
            return; // Do nothing if the report is still requested or in progress
        }
        if (record.status === "PDF") {
            window.open(record.pdf_url, "_blank");
        } else {
            navigate(`skin-report/${record.id}`);
        }
    }

    return (
        <Row
            gutter={[0, 24]}
            className="mt-6 bg-[--accent-primary] rounded-xl shadow-sm p-6 px-5 sm:px-6 border border-[--accent-secondary]"
        >
            {/* Header Section */}
            <Col span={24}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={4} className="text-white m-0">
                            {t("reports")}
                        </Title>
                    </Col>
                    <Col>
                        <RoundButtonWithIcon
                            text={t("request_report")}
                            icon={IoAddOutline}
                            onClick={() => {
                                setShowPopup(true);
                                console.log(requestReports);
                            }}
                        />
                    </Col>
                </Row>
            </Col>

            {/* Modal */}
            <Modal
                title={t("request_report")}
                open={showPopup}
                className="custom-dark-modal"
                onCancel={() => setShowPopup(false)}
                footer={null}
                width={700}
            >
                <Table
                    columns={requestReportColumn}
                    dataSource={reportRequestdataSource}
                    pagination={false}
                    className="custom-ant-table"
                    rowClassName={() => "border-b border-gray-700"}
                    locale={{
                        emptyText: (
                            <div className="text-gray-400 text-sm py-6 text-center">
                                {t("no_reports_requested")}
                            </div>
                        ),
                    }}
                />
                <div className="mt-4 flex justify-end">
                    <Button
                        type="primary"
                        disabled={isRequestCreated}
                        onClick={() => createNewReportRequest()}
                        style={{backgroundColor: "#8e44ad", borderColor: "#8e44ad"}}
                    >
                        {t("request_report")}
                    </Button>
                </div>
            </Modal>

            {/* Table Section */}
            <Col span={24}>
                <div className="overflow-x-auto">
                    <Table
                        columns={reportColumns}
                        dataSource={dataSource}
                        pagination={false}
                        onRow={(record) => ({
                            onClick: () => onRowClick(record),
                            style: {cursor: "pointer"},
                        })}
                        className="custom-ant-table"
                        rowClassName={() => "border-b border-gray-700"}
                        locale={{
                            emptyText: (
                                <div className="text-gray-400 text-sm py-6 text-center">
                                    {t("no_report_available")}
                                </div>
                            ),
                        }}
                    />
                </div>
            </Col>
        </Row>
    );
};

export default ReportTable;
